/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';

import { HostHdrMatch } from 'object-types';
import { HdrMatchOperation, IHostHdrMatch } from 'generated-types';
import { L10nService } from '@vmw/ngx-vip';

import * as globalL10n from 'global-l10n';
import * as l10n from './host-hdr-match-with-objects.l10n';
import './host-hdr-match-with-objects.component.less';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const preventConfigureValueSet = new Set([
    HdrMatchOperation.HDR_EXISTS,
    HdrMatchOperation.HDR_DOES_NOT_EXIST,
]);

/**
 * @description
 *     HostHdrMatch component, can also be used as LocationHdrMatch component. Used for plain objects/arrays.
 * @author alextsg
 */
@Component({
    selector: 'host-hdr-match-with-objects',
    templateUrl: './host-hdr-match-with-objects.component.html',
})
export class HostHdrMatchWithObjectsComponent implements OnInit {
    @Input()
    public config: IHostHdrMatch;

    @Input()
    public index = 0;

    /**
     * Match label passed to the MatchWrapperComponent
     */
    @Input()
    public label: string;

    /**
     * fieldName passed to the MatchWrapperComponent
     */
    @Input()
    public fieldName: string;

    /**
     * objectType passed to the MatchWrapperComponent
     */
    @Input()
    public objectType: string;

    /**
     * Disable any modifications to the fields.
     */
    @Input()
    public disabled = false;

    /**
     * Match ObjectType passed to this match component for fields in the template.
     * Enables reusing same component for different match types.
     * Value can be HostHdrMatch or LocationHdrMatch.
     */
    @Input()
    public matchObjectType = HostHdrMatch;

    /**
     * EventEmitter for removing a match, passed to the MatchWrapperComponent.
     */
    @Output()
    public onRemoveMatch = new EventEmitter<void>();

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    public showValueField = false;

    constructor(l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        if (this.canConfigureValue()) {
            this.showValueField = true;
        }
    }
    public handleCriteriaChange(): void {
        if (this.canConfigureValue()) {
            this.showValueField = true;
        } else {
            delete this.config.value;
            this.showValueField = false;
        }
    }

    public removeMatch(): void {
        this.onRemoveMatch.emit();
    }

    /**
     * True if the value field should not be configurable.
     */
    private canConfigureValue(): boolean {
        const { match_criteria: matchCriteria } = this.config;

        return matchCriteria && !preventConfigureValueSet.has(matchCriteria);
    }
}
